import { useMemo } from 'react';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetSummaryTrackedActivities } from 'api/queries/getSummaryTrackedActivities.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

export const useSummaryTrackedActivitiesComponentProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    table,
    endpoints,
}) => {
    const isInPinboard = Boolean(widgetId);
    const source = isInPinboard ? localFilters : { ...globalFilterData, sorting: table.sorting };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),
        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs, globalFilterData],
    );

    return {
        ui: {
            state: getState({ source, latestVersionId, contractId, projectId }),
            pills: useGetPills({ widgetId, source, latestVersionId }),
            isInPinboard,
        },
        api: {
            getTrackedActivities: {
                ...useQueryGetSummaryTrackedActivities(
                    getSummaryTrackedActivitiesPayload({
                        filters,
                        compareVersionId,
                        contractId,
                        latestVersionId,
                        projectId,
                    }),
                    endpoints.getTrackedItems,
                ),
                endpoint: endpoints.getTrackedItems,
            },
        },
    };
};

const getSummaryTrackedActivitiesPayload = ({ projectId, contractId, latestVersionId, compareVersionId, filters }) => ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    filter: {
        filters,
    },
});

const useGetPills = ({ widgetId, source, latestVersionId }) => {
    const pillsData = {
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });
    return pills;
};

const getState = ({ projectId, contractId, latestVersionId, source }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,

        // global filter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        //table
        sorting: source?.sorting,
    };
};
